import { useEffect } from 'react'
import { useForm } from '@inertiajs/react'
import { Loader2, AlertTriangle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { CredentialFieldset } from '@/frontend/components/Credentials/CredentialFieldset'
import type { Credential, CredentialField } from '@/frontend/pages/Dashboard/Integrations/types'

interface CredentialFormModalProps {
  open: boolean
  onClose: () => void
  postUrl: string
  title?: string
  onCreated?: () => void
  credential?: Credential
}

export const normalizeIdentifier = (value: string): string =>
  value.toLowerCase().replace(/\s+/g, '_')

interface FieldWithId extends CredentialField {
  _id: string
  _persisted: string
}

let fieldIdCounter = 0
const nextFieldId = () => `field-${++fieldIdCounter}`
const newField = (): FieldWithId => ({
  key: '',
  value: '',
  _id: nextFieldId(),
  _persisted: 'false',
})

/**
 * Modal de credencial da organização e das integrações (criar/editar). O
 * formulário em si vem do CredentialFieldset compartilhado — o MESMO do "Criar
 * nova" do portal do agente (ConnectCredentialModal) —, então a criação de
 * credencial é idêntica em todos os pontos de entrada. A Descrição (nota de uso)
 * aparece só na edição; a criação segue enxuta como o desenho aprovado.
 */
const CredentialFormModal = ({
  open,
  onClose,
  postUrl,
  title,
  onCreated,
  credential,
}: CredentialFormModalProps) => {
  const isEditing = !!credential
  const modalTitle = title || (isEditing ? 'Editar credencial' : 'Nova credencial')

  const form = useForm<{ name: string; description: string; fields: FieldWithId[] }>({
    name: '',
    description: '',
    fields: [newField()],
  })

  useEffect(() => {
    if (open && credential) {
      form.setData({
        name: credential.name,
        description: credential.description || '',
        fields: credential.fields.map((f) => ({
          ...f,
          value: '',
          _id: nextFieldId(),
          _persisted: 'true',
        })),
      })
    } else if (open && !credential) {
      form.setData({ name: '', description: '', fields: [newField()] })
    }
  }, [open, credential?.id])

  const addField = () => {
    form.setData('fields', [...form.data.fields, newField()])
  }

  const removeField = (index: number) => {
    if (form.data.fields.length <= 1) return
    form.setData('fields', form.data.fields.filter((_, i) => i !== index))
  }

  const updateField = (index: number, field: 'key' | 'value', value: string) => {
    const normalizedValue = field === 'key' ? normalizeIdentifier(value) : value
    form.setData('fields', form.data.fields.map((f, i) =>
      i === index ? { ...f, [field]: normalizedValue } : f
    ))
  }

  const isExistingField = (field: FieldWithId) => field._persisted === 'true'
  const hasInvalidFields = form.data.fields.some((field) => {
    if (!field.key.trim()) return true
    if (isEditing && isExistingField(field)) return false

    return !field.value.trim()
  })

  const handleSave = () => {
    form.transform((data) => ({
      ...data,
      fields: data.fields.map(({ _id, _persisted, ...rest }) => rest),
    }))

    const options = {
      preserveScroll: true,
      onSuccess: (page: { props: Record<string, unknown> }) => {
        if ((page.props.flash as { alert?: string } | undefined)?.alert) return
        form.reset()
        onClose()
        onCreated?.()
      },
    }

    if (isEditing) {
      form.patch(postUrl, options)
    } else {
      form.post(postUrl, options)
    }
  }

  const handleClose = () => {
    form.reset()
    form.clearErrors()
    onClose()
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle>{modalTitle}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {isEditing && (
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>Os valores estão mascarados por segurança. Deixe em branco para manter os valores atuais.</AlertDescription>
            </Alert>
          )}

          <CredentialFieldset
            idPrefix="credential"
            name={form.data.name}
            onNameChange={(value) => form.setData('name', normalizeIdentifier(value))}
            nameError={form.errors.name}
            nameDisabled={isEditing}
            fields={form.data.fields}
            onFieldChange={updateField}
            onAddField={addField}
            onRemoveField={removeField}
            fieldsError={form.errors.fields}
            isFieldDisabled={(index) => isEditing && isExistingField(form.data.fields[index])}
            valuePlaceholder={
              isEditing
                ? (index) => (isExistingField(form.data.fields[index]) ? '••••••••' : 'Cole a chave/segredo')
                : undefined
            }
            afterName={
              isEditing ? (
                <div className="grid gap-2">
                  <Label htmlFor="credential-description">Descrição</Label>
                  <Textarea
                    id="credential-description"
                    value={form.data.description}
                    onChange={(e) => form.setData('description', e.target.value)}
                    placeholder="Instruções para o agente sobre como e quando esta chave deve ser usada"
                  />
                  {form.errors.description && <p className="text-sm text-destructive">{form.errors.description}</p>}
                </div>
              ) : undefined
            }
          />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Cancelar</Button>
          <Button
            onClick={handleSave}
            disabled={form.processing || !form.data.name.trim() || hasInvalidFields}
          >
            {form.processing && <Loader2 className="h-4 w-4 animate-spin" />}
            {form.processing ? 'Salvando…' : 'Salvar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CredentialFormModal
